import { Box, Paper, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import moment from 'moment';
import {
  Datagrid,
  Filter,
  FunctionField,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  TextField,
  useListContext,
  AutocompleteInput,} from 'react-admin';
import { Permission, ReportList } from "../../components";
import { MonthYearInput } from '../../components/Inputs';
import ListPagination from '../../components/ListPagination';
import { PERMISSIONS } from "../../constants";
import { ConcentratorBrandType } from '../../models/concentratorBrandType';
import { filterToQuery } from '../../utils';

const translateConcentratorBrand = (brand) => {
  if (!brand || brand === 'Não identificado') return 'Não identificado';
  return ConcentratorBrandType.translateType(brand) || brand;
};

const statusColors = {
  'Faturando': '#4caf50',
  'Ativo Sem Faturamento': '#ff9800',
  'Cancelado': '#f44336'
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const SummaryTable = ({ summaryData }) => {
  if (!summaryData) return null;

  const statusRows = [
    { key: 'faturando', label: 'Faturando', color: statusColors['Faturando'] },
    { key: 'ativoSemFaturamento', label: 'Ativo Sem Faturamento', color: statusColors['Ativo Sem Faturamento'] },
    { key: 'cancelado', label: 'Cancelado', color: statusColors['Cancelado'] },
  ];

  const totals = statusRows.reduce((acc, row) => ({
    count: acc.count + (summaryData[row.key]?.count || 0),
    faturamento: acc.faturamento + (summaryData[row.key]?.faturamento || 0),
    ctt: acc.ctt + (summaryData[row.key]?.ctt || 0)
  }), { count: 0, faturamento: 0, ctt: 0 });

  return (
    <Paper sx={{ mb: 2 }} elevation={1}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#fafafa' }}>
            <TableCell><strong>Status</strong></TableCell>
            <TableCell align="right"><strong>Qtd Postos</strong></TableCell>
            <TableCell align="right"><strong>Total Faturamento</strong></TableCell>
            <TableCell align="right"><strong>Total Valor CTT</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {statusRows.map(row => (
            <TableRow key={row.key} hover>
              <TableCell style={{ color: row.color, fontWeight: 'bold' }}>
                {row.label}
              </TableCell>
              <TableCell align="right">{summaryData[row.key]?.count || 0}</TableCell>
              <TableCell align="right">{formatCurrency(summaryData[row.key]?.faturamento || 0)}</TableCell>
              <TableCell align="right">{formatCurrency(summaryData[row.key]?.ctt || 0)}</TableCell>
            </TableRow>
          ))}
          <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
            <TableCell><strong>TOTAL</strong></TableCell>
            <TableCell align="right"><strong>{totals.count}</strong></TableCell>
            <TableCell align="right"><strong>{formatCurrency(totals.faturamento)}</strong></TableCell>
            <TableCell align="right"><strong>{formatCurrency(totals.ctt)}</strong></TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Paper>
  );
};

const StationsCTTValueList = () => {
  const props = useListContext();

  let reportData = [];
  let summaryData = null;

  if (props.data) {
    const info = props.data[0];
    if (info) {
      reportData = info.data || [];
      summaryData = info.summaryData;
    }
  }

  return (
    <Box sx={{ p: 2 }}>
      <SummaryTable summaryData={summaryData} />
      <Datagrid bulkActionButtons={false} data={reportData} total={1}>
        <ReferenceField
          label="Rede"
          source="chainId"
          reference="chains"
          link={false}
          sortable={false}
        >
          <TextField source="name" />
        </ReferenceField>
        <TextField source="name" label="Posto" sortable={false} />
        <TextField source="cnpj" label="CNPJ" sortable={false} />
        <FunctionField
          label="Marca Concentrador"
          sortable={false}
          render={record => translateConcentratorBrand(record.concentratorBrand)}
        />
        <FunctionField
          label="Status"
          sortable={false}
          render={record => (
            <span style={{ color: statusColors[record.status], fontWeight: 'bold' }}>
              {record.status}
            </span>
          )}
        />
        <FunctionField
          label="Valor Faturamento"
          sortable={false}
          textAlign="right"
          render={record => formatCurrency(record.clientBillValue)}
        />
        <FunctionField
          label="Valor CTT"
          sortable={false}
          textAlign="right"
          render={record => formatCurrency(record.valorCTT)}
        />
      </Datagrid>
    </Box>
  );
};

const statusChoices = [
  { id: 'Faturando', name: 'Faturando' },
  { id: 'Ativo Sem Faturamento', name: 'Ativo Sem Faturamento' },
  { id: 'Cancelado', name: 'Cancelado' },
];

const ListFilters = props => {
  return (
    <Filter {...props}>
      <MonthYearInput
        source="monthDate"
        label="Competência"
        format={date => moment(date).startOf('month').toISOString()}
        maxDate={moment()}
        alwaysOn
      />
      <ReferenceInput source="chainId" reference="chains" alwaysOn>
        <AutocompleteInput filterToQuery={filterToQuery} label="Rede" optionText="name" />
      </ReferenceInput>
      <SelectInput
        source="status"
        label="Status"
        choices={statusChoices}
        emptyText="Todos"
        alwaysOn
      />
    </Filter>
  );
};

const stationsCTTValueExport = async (data, fetchRelatedRecords) => {
  if (!data || data.length === 0 || !data[0].data) {
    return [];
  }

  const reportData = data[0].data;

  // Fetch related data (chain names)
  const relations = [
    { field: 'chainId', resource: 'chains' }
  ];
  const fetchData = await fetchRelatedRecords(relations, reportData);
  const chains = fetchData.find(r => r.resource === 'chains')?.results || [];

  // Map to export format
  return reportData.map(item => {
    const chain = chains.find(c => c.id === item.chainId);
    return {
      'Rede': chain?.name || '',
      'Posto': item.name || '',
      'CNPJ': item.cnpj || '',
      'Marca Concentrador': translateConcentratorBrand(item.concentratorBrand),
      'Status': item.status,
      'Valor Faturamento': item.clientBillValue ?? 0,
      'Valor CTT': item.valorCTT ?? 0
    };
  });
};

const ReportStationsCTTValue = props => {
  const filterValues = {
    monthDate: moment().subtract(1, 'month').startOf('month').toISOString()
  };

  return (
    <Permission permission={PERMISSIONS.ADMIN}>
      <ReportList
        {...props}
        filterDefaultValues={filterValues}
        basePath="report-stations-ctt-value"
        resource="reports/stations-ctt-value"
        title="Relatório de Valores CTT"
        filters={<ListFilters />}
        perPage={500}
        pagination={<ListPagination />}
        titleOnPrint="Relatório de Valores CTT - ConectTec"
        customExporter={stationsCTTValueExport}
        landscape={true}
      >
        <StationsCTTValueList />
      </ReportList>
    </Permission>
  );
};

export default ReportStationsCTTValue;
